import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { Home, Briefcase, Check, ArrowRight } from "lucide-react";
import PwaIcon from "../components/PwaIcon";

/**
 * ChoosePlan — shown right after sign-in (before onboarding) when the user has
 * no `account_type`. The choice is permanent for non-admins and decides the
 * plan limits (max pantries / members / items) configured by the super admin.
 */
export default function ChoosePlan() {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const limits = (user?.plan_limits) || {};

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post("/auth/account-type", { account_type: picked });
      await refresh();
      navigate(user?.household_id ? "/dashboard" : "/onboarding", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const Card = ({ value, icon: Icon, title, desc, perks }) => {
    const active = picked === value;
    return (
      <button
        onClick={() => setPicked(value)}
        className={`w-full text-left rounded-3xl border-2 p-5 transition active:scale-[0.99] ${
          active ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-950"
        }`}
        data-testid={`plan-card-${value}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${active ? "bg-fresh-green text-black" : "bg-zinc-100 text-zinc-950"}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-xl font-medium">{title}</div>
            <div className={`text-xs mt-0.5 ${active ? "text-white/70" : "text-zinc-500"}`}>{desc}</div>
          </div>
          {active && <Check className="w-5 h-5" strokeWidth={3} />}
        </div>
        <ul className={`mt-4 space-y-1.5 text-sm ${active ? "text-white/80" : "text-zinc-700"}`}>
          {perks.map((p) => (
            <li key={p} className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-fresh-green" : "bg-zinc-300"}`} />
              {p}
            </li>
          ))}
        </ul>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="choose-plan-page">
      <div className="px-7 pt-safe pt-12">
        <div className="flex items-center gap-2">
          <PwaIcon size={28} />
          <span className="font-heading text-base font-semibold">Niper</span>
        </div>
        <div className="mt-10 text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-green-text">
          {t("plan.eyebrow")}
        </div>
        <h1 className="mt-2 font-heading text-4xl font-medium leading-tight">
          {t("plan.title")}
        </h1>
        <p className="mt-3 text-zinc-500 text-sm leading-relaxed">{t("plan.subtitle")}</p>

        <div className="mt-8 space-y-4">
          <Card
            value="private"
            icon={Home}
            title={t("plan.private.title")}
            desc={t("plan.private.desc")}
            perks={[
              t("plan.bullet.households", { n: limits.private?.max_households ?? 1 }),
              t("plan.bullet.members", { n: limits.private?.max_members_per_household ?? 4 }),
              t("plan.bullet.items", { n: limits.private?.max_items ?? 200 }),
            ]}
          />
          <Card
            value="business"
            icon={Briefcase}
            title={t("plan.business.title")}
            desc={t("plan.business.desc")}
            perks={[
              t("plan.bullet.households", { n: limits.business?.max_households ?? 3 }),
              t("plan.bullet.members", { n: limits.business?.max_members_per_household ?? 20 }),
              t("plan.bullet.items", { n: limits.business?.max_items ?? 2000 }),
            ]}
          />
        </div>

        <button
          onClick={submit}
          disabled={!picked || busy}
          className="mt-8 w-full h-14 rounded-2xl bg-zinc-950 text-white font-bold disabled:opacity-40 active:scale-[0.99] flex items-center justify-center gap-2"
          data-testid="plan-submit-btn"
        >
          {busy ? t("common.loading") : t("plan.continue")}
          {!busy && <ArrowRight className="w-4 h-4" />}
        </button>

        <p className="mt-4 text-center text-[11px] text-zinc-400">{t("plan.note")}</p>
      </div>
    </div>
  );
}
